import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Avatar, message } from "antd";
import { Bold, Italic, Underline, List, Link2 } from "lucide-react";
import ChatService from "../../../Services/ChatService";

// The chat message box: the same rich text editor as the task Description (Quill: bold, italic, underline,
// bullets, links) in place of the plain box. The chat's own attachment, emoji and voice-note buttons stay.
// Enter sends (in a bullet list it adds the next bullet), Shift+Enter adds a line. With `mentionUsers`
// ({ id, display }) typing "@" suggests people and puts "@Name" in the text, which the send turns into a
// mention as before. A pasted picture goes into the text at the cursor, any other pasted file to
// `onPasteFiles` (sent as an attachment); text copied together with pictures keeps its pictures in their
// places (see takeInlineImages).
// onChange(html, text): `text` is the message as plain text (lines, "• " bullets) -- what the mobile app,
// notifications, previews and search keep using; `html` carries the formatting for the web chat.

const FORMATS = ["bold", "italic", "underline", "strike", "link", "list", "image"];

// The message as plain text: a line per paragraph, "• " (or "1. ") before list items
export const chatPlainText = (html) => {
  const s = String(html || "");
  if (!s) return "";
  const doc = new DOMParser().parseFromString(s, "text/html");
  const lines = [];
  Array.from(doc.body.children).forEach((block) => {
    if (block.tagName === "UL" || block.tagName === "OL") {
      Array.from(block.children).forEach((li, i) => lines.push(`${block.tagName === "OL" ? `${i + 1}. ` : "• "}${li.textContent}`));
    } else {
      lines.push(block.textContent);
    }
  });
  // kept as typed (spaces and blank lines too), like the old box
  return lines.join("\n").replace(/ /g, " ");
};

// Formatting beyond plain lines (or pictures in the text): only then is the html sent with the message
export const hasChatFormatting = (html) => /<(strong|em|u|s|a|ol|ul|img)\b/i.test(String(html || ""));

// ── Pictures in the text (pasted together with text, in the place they were copied) ──
// In the box they are data: pictures. Sending makes each one a file of the message (uploaded with it, so the
// mobile app gets it as an attachment) and the html points to it as "att:<file name without extension>".

// A file's key for that ("inline-1730275200000-1.png" -> "inline-1730275200000-1"; upload may change .png to .jpg)
export const attKey = (f) => String(f?.name || f?.originalName || "").replace(/\.[^.]+$/, "");

// The keys of the pictures a message shows in its text (those files are not listed again below it)
export const inlineImageKeys = (html) => new Set(Array.from(String(html || "").matchAll(/src="att:([\w-]+)"/g), (m) => m[1]));

// The box's pictures for sending: { html (pictures as att: keys), files (to upload with the message) }
export const takeInlineImages = (html) => {
  const s = String(html || "");
  if (!/<img\b/i.test(s)) return { html: s, files: [] };
  const doc = new DOMParser().parseFromString(s, "text/html");
  const files = [];
  const stamp = Date.now();
  doc.querySelectorAll("img").forEach((img) => {
    const m = (img.getAttribute("src") || "").match(/^data:(image\/[a-z0-9.+-]+);base64,(.+)$/i);
    if (!m || /svg/i.test(m[1])) { img.remove(); return; }
    const key = `inline-${stamp}-${files.length + 1}`;
    const bytes = Uint8Array.from(atob(m[2]), (c) => c.charCodeAt(0));
    files.push(new File([bytes], `${key}.${(m[1].split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: m[1] }));
    img.setAttribute("src", `att:${key}`);
  });
  return { html: doc.body.innerHTML, files };
};

// A saved message for the box (editing it): its pictures stay as its attachments (shown as the edit's file
// pills), so they come out of the text
export const chatHtmlForEdit = (html) => (html ? String(html).replace(/<img\b[^>]*>/gi, "") : html);

// A picture for the box as a data: URL (the editor keeps only http/https/data pictures); big ones made smaller
const fileToDataUrl = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onerror = () => resolve(null);
  reader.onload = () => {
    const src = reader.result;
    if (file.size < 1024 * 1024) { resolve(src); return; }
    const img = new Image();
    img.onerror = () => resolve(src);
    img.onload = () => {
      const MAX = 1600;
      let w = img.width, h = img.height;
      if (w > MAX || h > MAX) { const r = Math.min(MAX / w, MAX / h); w = Math.round(w * r); h = Math.round(h * r); }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = src;
  };
  reader.readAsDataURL(file);
});

// A plain message for the box (editing one): mentions as @Name, a paragraph per line
export const chatTextToHtml = (text) => {
  const esc = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return String(text || "")
    .replace(/@\[([^\]]+)\]\(\d+\)/g, "@$1")
    .split(/\r?\n/)
    .map((line) => `<p>${line ? esc(line) : "<br>"}</p>`)
    .join("");
};

// The pictures in copied html (a web page, a document, another chat), in the order they were copied
const copiedImageSources = (html) => {
  if (!html || !/<img\b/i.test(html)) return [];
  const doc = new DOMParser().parseFromString(html, "text/html");
  return Array.from(doc.images).map((img) => img.getAttribute("src") || "").filter(Boolean).slice(0, 10);
};

const pastedImageName = (type, i = 0) => {
  const ext = (String(type).split("/")[1] || "png").split(/[^a-z0-9]/i)[0].replace("jpeg", "jpg") || "png";
  return `pasted-image-${Date.now()}${i ? `-${i}` : ""}.${ext}`;
};

// A copied picture as a file: read by the browser when it may (data:, this site's blob:, sites that allow
// it), else fetched by the server. null when it can't be had (file:, cid:, private sites...).
const copiedImageFile = async (src, i) => {
  const toFile = (blob) => (blob && /^image\//.test(blob.type) && !/svg/.test(blob.type) ? new File([blob], pastedImageName(blob.type, i), { type: blob.type }) : null);
  if (!/^(https?:|data:image\/|blob:)/i.test(src)) return null;
  try {
    const r = await fetch(src, { credentials: "omit" });
    if (r.ok) {
      const f = toFile(await r.blob());
      if (f) return f;
    }
  } catch (e) { /* not readable from the browser (the site doesn't allow it) */ }
  if (!/^https?:/i.test(src)) return null;
  try {
    const res = await ChatService.getCopiedImage(src);
    if (!res?.Good || !res.data) return null;
    const bytes = Uint8Array.from(atob(res.data), (c) => c.charCodeAt(0));
    return toFile(new Blob([bytes], { type: res.type }));
  } catch (e) {
    return null;
  }
};

const styles = (
  <style>{`
    /* the old box's one-line height, kept whatever other pages' editor styles say */
    .chat-rich-input { width: 100%; min-width: 0; min-height: 24px; }
    .chat-rich-input .quill, .chat-rich-input .ql-container { width: 100%; min-height: 24px !important; height: auto !important; }
    .chat-rich-input .ql-container.ql-snow { border: none !important; font-family: inherit !important; font-size: 14px; }
    .chat-rich-input .ql-container .ql-editor {
      padding: 0 !important; min-height: 24px !important; max-height: 100px !important; height: auto !important; overflow-y: auto !important;
      font-size: 14px !important; line-height: 1.5 !important; color: #1d1d1d; cursor: text;
    }
    .chat-rich-input .ql-editor p, .chat-rich-input .ql-editor ul, .chat-rich-input .ql-editor ol { margin: 0 !important; }
    /* "Type anything..." (the task editors hide every Quill placeholder on the page) */
    .chat-rich-input .ql-container .ql-editor.ql-blank::before {
      display: block !important; left: 0 !important; right: 0 !important; font-style: normal !important; color: #b0b0b0 !important; font-size: 14px !important;
    }
    .chat-rich-input .ql-editor a { color: #1a73e8; text-decoration: underline; }
    .chat-rich-input .ql-editor img { display: block; max-width: 200px; max-height: 90px; border-radius: 6px; margin: 2px 0; }
    /* a pasted picture the editor couldn't keep, while it is being read (no broken-picture icon) */
    .chat-rich-input .ql-editor img[src="//:0"] { display: none !important; }
    .chat-rich-input.disabled .ql-editor { cursor: default; }
    .chat-mention-list {
      background: #fff; border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; overflow-y: auto; max-height: 240px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.1); min-width: 200px; max-width: 280px;
    }
    .chat-mention-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid rgba(0,0,0,0.05); cursor: pointer; background: #fff; }
    .chat-mention-item.active { background: #f5f5f5; }
    .chat-link-apply-btn { background-color: #0DDFAB !important; border: none !important; color: #000 !important; font-weight: 600 !important; border-radius: 8px !important; padding: 6px 16px !important; font-size: 13px !important; }
  `}</style>
);

const ChatRichInput = forwardRef(({ value, onChange, onEnter, onBlur, placeholder, disabled = false, mentionUsers = null, onPasteFiles }, ref) => {
  const quillRef = useRef(null);
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  const onPasteFilesRef = useRef(onPasteFiles);
  onPasteFilesRef.current = onPasteFiles;
  const mentionUsersRef = useRef(mentionUsers);
  mentionUsersRef.current = mentionUsers;
  // the open "@" suggestions: where the "@" is, the matching people and the highlighted one
  const [mention, setMention] = useState(null);
  const mentionRef = useRef(null);
  mentionRef.current = mention;
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPopoverPos, setLinkPopoverPos] = useState({ top: 0, left: 0 });
  const linkRangeRef = useRef(null);

  const getEditor = () => {
    try { return quillRef.current?.getEditor?.(); } catch (e) { return null; }
  };

  // react-quill reads the placeholder only when the editor is made ("Select someone..." -> "Type anything...")
  useEffect(() => {
    const quill = getEditor();
    if (quill) quill.root.setAttribute("data-placeholder", placeholder || "");
  }, [placeholder]);

  const pickMention = (user) => {
    const quill = getEditor();
    const m = mentionRef.current;
    if (!quill || !m || !user) return;
    const range = quill.getSelection(true);
    quill.deleteText(m.start, Math.max(0, range.index - m.start), "user");
    const text = `@${user.display} `;
    quill.insertText(m.start, text, "user");
    quill.setSelection(m.start + text.length, 0, "user");
    setMention(null);
  };
  const pickMentionRef = useRef(pickMention);
  pickMentionRef.current = pickMention;

  // "@" + what follows it before the cursor -> the people whose name contains it
  useEffect(() => {
    const quill = getEditor();
    if (!quill) return undefined;
    const update = () => {
      const users = mentionUsersRef.current;
      const range = quill.getSelection();
      if (!users || !users.length || !range || range.length) { setMention(null); return; }
      const from = Math.max(0, range.index - 40);
      const m = quill.getText(from, range.index - from).match(/(^|\s)@([^@\n]{0,30})$/);
      if (!m) { setMention(null); return; }
      const query = m[2].toLowerCase();
      const items = users.filter((u) => String(u.display || "").toLowerCase().includes(query)).slice(0, 50);
      if (!items.length) { setMention(null); return; }
      const box = quill.container.getBoundingClientRect();
      const at = quill.getBounds(range.index);
      setMention((prev) => ({
        start: range.index - m[2].length - 1,
        query,
        items,
        active: prev && prev.query === query ? Math.min(prev.active, items.length - 1) : 0,
        bottom: window.innerHeight - (box.top + at.top) + 6,
        left: Math.max(8, Math.min(box.left + at.left, window.innerWidth - 290)),
      }));
    };
    const handler = (eventName) => { if (eventName === "text-change" || eventName === "selection-change") update(); };
    quill.on("editor-change", handler);
    return () => quill.off("editor-change", handler);
  }, []);

  // Pasting, as the task Description does: a copied picture goes into the text where the cursor is; any
  // other copied file goes as an attachment. Text copied together with pictures (a web page, a document,
  // another chat) goes in as it was copied, the pictures in their places; each picture is then made one the
  // box can send (see takeInlineImages).
  useEffect(() => {
    const quill = getEditor();
    if (!quill) return undefined;
    const addFiles = (files) => { if (files.length && onPasteFilesRef.current) onPasteFilesRef.current(files); };
    // Pictures some apps keep private to their own page (Google Chat / Hangouts, WhatsApp Web, Gmail...) come
    // only as a link nobody else can open; "Copy image" on the picture itself gives the picture.
    const warnMissed = (missed) => {
      if (missed > 0) {
        const it = missed > 1 ? "them" : "it";
        message.warning(`${missed} picture${missed > 1 ? "s" : ""} couldn't be copied from there. Right-click ${it} > Copy image, then paste ${it} here.`, 6);
      }
    };
    const isPicture = (f) => f.type.startsWith("image/") && !/svg/i.test(f.type);
    // pictures into the text at `at` (where the cursor was when pasting), one after another
    const insertPictures = async (pictures, at) => {
      let index = at;
      for (const f of pictures) {
        const dataUrl = await fileToDataUrl(f);
        const ed = getEditor();
        if (!dataUrl || !ed) continue;
        index = Math.min(index, Math.max(0, ed.getLength() - 1));
        ed.insertEmbed(index, "image", dataUrl, "user");
        index += 1;
        ed.setSelection(index, 0, "user");
      }
    };
    // After Quill has put the paste in (a moment later): its pictures that aren't data: yet (another site's, or
    // ones the editor couldn't keep -- this blob:, file: -> "//:0") are read and put back as data: pictures in
    // the same place; one nobody can read comes out. A Word document's own pictures can only be had as the
    // clipboard's picture.
    const settlePastedImages = (sources, clipboardImages) => {
      const wanted = sources.filter((s) => !/^data:/i.test(s));
      setTimeout(async () => {
        const ed = getEditor();
        if (!ed) return;
        const pasted = Array.from(ed.root.querySelectorAll("img")).filter((img) => !/^data:/i.test(img.getAttribute("src") || ""));
        const spare = [...clipboardImages];
        let missed = 0;
        for (let i = 0; i < pasted.length; i += 1) {
          const src = pasted[i].getAttribute("src") || "";
          const source = /^https?:/i.test(src) ? src : (wanted.length === pasted.length ? wanted[i] : "");
          let file = source ? await copiedImageFile(source, i) : null;
          if (!file && spare.length) file = spare.shift();
          const dataUrl = file ? await fileToDataUrl(file) : null;
          const blot = ReactQuill.Quill.find(pasted[i]);
          if (!blot || !blot.scroll) continue; // taken out meanwhile
          const index = ed.getIndex(blot);
          ed.deleteText(index, 1, "user");
          if (dataUrl) ed.insertEmbed(index, "image", dataUrl, "user");
          else missed += 1;
        }
        warnMissed(missed);
      }, 60);
    };
    // only pictures copied (as html, without a picture file): read each and put it in at the cursor
    const pictureSources = async (sources, at) => {
      const files = [];
      for (let i = 0; i < sources.length; i += 1) {
        const f = await copiedImageFile(sources[i], i);
        if (f) files.push(f);
      }
      await insertPictures(files, at);
      warnMissed(sources.length - files.length);
    };
    const onPaste = (e) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const files = Array.from(cd.items || []).filter((i) => i.kind === "file").map((i) => i.getAsFile()).filter(Boolean)
        .map((f, i) => (f.type.startsWith("image/") ? new File([f], pastedImageName(f.type, i), { type: f.type }) : f));
      const sources = copiedImageSources(cd.getData("text/html"));
      if (String(cd.getData("text/plain") || "").trim()) {
        if (sources.length) settlePastedImages(sources, files.filter(isPicture));
        return; // Quill pastes the text and its pictures
      }
      if (!files.length && !sources.length) return;
      e.preventDefault();
      e.stopPropagation();
      const sel = quill.getSelection();
      const at = sel ? sel.index : Math.max(0, quill.getLength() - 1);
      const pictures = files.filter(isPicture);
      addFiles(files.filter((f) => !isPicture(f)));
      if (pictures.length) insertPictures(pictures, at);
      else if (!files.length) pictureSources(sources, at);
    };
    quill.root.addEventListener("paste", onPaste, true);
    return () => quill.root.removeEventListener("paste", onPaste, true);
  }, []);

  // Made once and kept: react-quill 2.0 rebuilds the editor when it gets a new `modules` object and is left
  // with an empty box nobody can type in (it happened on every live reload). The handlers only read refs.
  const modulesRef = useRef(null);
  if (!modulesRef.current) modulesRef.current = {
    toolbar: false,
    clipboard: { matchVisual: false },
    keyboard: {
      bindings: {
        mentionDown: {
          key: 40,
          handler() {
            const m = mentionRef.current;
            if (!m) return true;
            setMention({ ...m, active: (m.active + 1) % m.items.length });
            return false;
          },
        },
        mentionUp: {
          key: 38,
          handler() {
            const m = mentionRef.current;
            if (!m) return true;
            setMention({ ...m, active: (m.active - 1 + m.items.length) % m.items.length });
            return false;
          },
        },
        mentionClose: {
          key: 27,
          handler() {
            if (!mentionRef.current) return true;
            setMention(null);
            return false;
          },
        },
        enterToSend: {
          key: 13,
          shiftKey: false,
          handler(range, context) {
            const m = mentionRef.current;
            if (m) { pickMentionRef.current(m.items[m.active]); return false; }
            if (context.format.list) return true;
            if (onEnterRef.current) onEnterRef.current();
            return false;
          },
        },
        autoLink: {
          key: 32,
          handler(range) {
            const q = this.quill;
            const m = q.getText(0, range.index).match(/(\S+)$/);
            if (m && /^(https?:\/\/|www\.)\S+$/.test(m[1])) {
              const wordStart = range.index - m[1].length;
              if (!q.getFormat(wordStart, m[1].length).link) {
                q.formatText(wordStart, m[1].length, "link", /^https?:\/\//i.test(m[1]) ? m[1] : "https://" + m[1], "user");
              }
            }
            return true;
          },
        },
      },
    },
  };

  const openLinkPopover = (e) => {
    const quill = getEditor();
    if (!quill) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const popW = 320, popH = 60;
    let top = rect.top - popH - 8;
    if (top < 8) top = rect.bottom + 8;
    const left = Math.max(8, Math.min(rect.left - popW + rect.width, window.innerWidth - popW - 8));
    const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
    linkRangeRef.current = { index: range.index, length: range.length };
    const format = quill.getFormat(range);
    if (format.link) {
      // the whole link the cursor is in
      const [leaf] = quill.getLeaf(range.index);
      let blot = leaf;
      while (blot && blot.statics.blotName !== "link" && blot !== quill.scroll) blot = blot.parent;
      if (blot && blot.statics.blotName === "link") linkRangeRef.current = { index: quill.getIndex(blot), length: blot.length() };
      setLinkUrl(format.link);
    } else {
      setLinkUrl("");
    }
    setLinkPopoverPos({ top, left });
    setShowLinkInput((v) => !v);
  };

  const closeLink = () => { setShowLinkInput(false); setLinkUrl(""); };

  const applyLink = (url) => {
    const quill = getEditor();
    const target = linkRangeRef.current;
    if (!quill || !target) { closeLink(); return; }
    const text = String(url || "").trim();
    const href = text ? (/^(https?:|mailto:)/i.test(text) ? text : `https://${text}`) : false;
    if (target.length) {
      quill.formatText(target.index, target.length, "link", href, "user");
    } else if (href) {
      // nothing selected: the link goes in as its own text
      quill.insertText(target.index, text, "link", href, "user");
      quill.setSelection(target.index + text.length, 0, "user");
    }
    closeLink();
  };

  useImperativeHandle(ref, () => ({
    focus: () => getEditor()?.focus(),
    // text at the cursor (emoji, "@"), as if typed
    insertText: (text) => {
      const quill = getEditor();
      if (!quill) return;
      const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
      if (range.length) quill.deleteText(range.index, range.length, "user");
      quill.insertText(range.index, text, "user");
      quill.setSelection(range.index + text.length, 0, "user");
    },
    toggleFormat: (name) => {
      const quill = getEditor();
      if (!quill) return;
      quill.getSelection(true);
      const f = quill.getFormat();
      if (name === "list") quill.format("list", f.list === "bullet" ? false : "bullet", "user");
      else quill.format(name, !f[name], "user");
    },
    openLink: openLinkPopover,
  }));

  const mentionPortal = mention && ReactDOM.createPortal(
    <div className="chat-mention-list" style={{ position: "fixed", bottom: mention.bottom, left: mention.left, zIndex: 999999 }} onMouseDown={(e) => e.preventDefault()}>
      {mention.items.map((u, i) => (
        <div
          key={u.id}
          className={`chat-mention-item ${i === mention.active ? "active" : ""}`}
          onMouseEnter={() => setMention((m) => (m ? { ...m, active: i } : m))}
          onClick={() => pickMention(u)}
        >
          <Avatar size={24} style={{ background: "#0DDFAB", color: "#000", fontSize: 10, flexShrink: 0 }}>{u.display?.charAt(0)}</Avatar>
          <span style={{ fontSize: 13, color: "#1d1d1d", fontWeight: 500 }}>{u.display}</span>
        </div>
      ))}
    </div>,
    document.body
  );

  // the same link box as the task Description
  const linkPortal = showLinkInput && ReactDOM.createPortal(
    <div>
      <div style={{ position: "fixed", inset: 0, zIndex: 999998 }} onMouseDown={(e) => { e.preventDefault(); closeLink(); }} />
      <div style={{ position: "fixed", top: linkPopoverPos.top, left: linkPopoverPos.left, zIndex: 999999, background: "#fff", border: "1.5px solid #1D1D1D", borderRadius: "10px", padding: "4px", width: "320px", boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
        <div className="d-flex gap-2 align-items-center">
          <input
            type="text"
            className="no-focus-ring"
            autoFocus
            placeholder="Paste or type a link..."
            style={{ border: "none", outline: "none", boxShadow: "none", fontSize: "14px", padding: "6px 8px", background: "transparent", flex: 1, minWidth: 0 }}
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(linkUrl); } if (e.key === "Escape") closeLink(); }}
          />
          {linkUrl && (
            <button type="button" className="d-flex align-items-center justify-content-center" style={{ padding: "4px 8px", borderRadius: "6px", border: "1.5px solid #ff4d4f", color: "#ff4d4f", background: "#fff" }} title="Remove link" onClick={() => applyLink("")}>
              <i className="uil uil-link-broken" style={{ fontSize: "18px" }} />
            </button>
          )}
          <button type="button" className="chat-link-apply-btn" onClick={() => applyLink(linkUrl)}>Apply</button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <div className={`chat-rich-input ${disabled ? "disabled" : ""}`}>
      {styles}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={(html) => { if (onChange) onChange(html, chatPlainText(html)); }}
        onBlur={() => { setMention(null); if (onBlur) onBlur(); }}
        placeholder={placeholder}
        readOnly={disabled}
        modules={modulesRef.current}
        formats={FORMATS}
      />
      {mentionPortal}
      {linkPortal}
    </div>
  );
});

ChatRichInput.displayName = "ChatRichInput";

// The formatting buttons for the chat toolbar row, as the same icon boxes as its attachment / emoji / mic
// buttons. mousedown is stopped so the text selection stays in the box.
const FORMAT_BUTTONS = [
  { title: "Bold", Icon: Bold, act: (ed) => ed.toggleFormat("bold") },
  { title: "Italic", Icon: Italic, act: (ed) => ed.toggleFormat("italic") },
  { title: "Underline", Icon: Underline, act: (ed) => ed.toggleFormat("underline") },
  { title: "Bullet list", Icon: List, act: (ed) => ed.toggleFormat("list") },
  { title: "Link", Icon: Link2, act: (ed, e) => ed.openLink(e) },
];

export const ChatFormatBar = ({ editorRef }) => (
  <>
    {FORMAT_BUTTONS.map(({ title, Icon, act }) => (
      <div
        key={title}
        className="toolbar-icon-box"
        title={title}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => { if (editorRef.current) act(editorRef.current, e); }}
      >
        <Icon size={15} strokeWidth={2.5} />
      </div>
    ))}
  </>
);

export default ChatRichInput;
